'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, AlertTriangle, DollarSign, RefreshCw, ChevronDown, ChevronUp, Flag, Shield, Activity, Download, Search, Zap, Lock, AlertCircle } from 'lucide-react';


import MetricCard from '@/components/ui/MetricCard';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeAlerts } from '@/hooks/useRealtimeAlerts';
import RealtimeFeedPanel from '@/components/ui/RealtimeFeedPanel';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Milestone {
  id: string;
  label: string;
  target: number;
  achieved: number;
  status: 'met' | 'partial' | 'unmet';
}

interface SettlementItem {
  id: string;
  batchRef: string;
  campaign: string;
  issuer: string;
  amount: string;
  amountRaw: number;
  investors: number;
  milestones: Milestone[];
  status: 'pending-approval' | 'approved' | 'executing' | 'completed' | 'rejected' | 'on-hold';
  priority: 'critical' | 'high' | 'normal';
  submittedBy: string;
  submittedAt: string;
  dueDate: string;
  complianceCleared: boolean;
  kycCleared: boolean;
  escrowReleased: boolean;
  bankDetails: string;
  notes: string;
  auditLog: { ts: string; actor: string; action: string }[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const mockSettlements: SettlementItem[] = [
  {
    id: 'sp-001',
    batchRef: 'SP-20260820-007',
    campaign: 'ClearPath Clean Energy Fund III',
    issuer: 'ClearPath Renewables Ltd',
    amount: '$7,840,000',
    amountRaw: 7840000,
    investors: 198,
    milestones: [
      { id: 'm1', label: 'Minimum raise (80%)', target: 6400000, achieved: 7840000, status: 'met' },
      { id: 'm2', label: 'Compliance sign-off', target: 1, achieved: 1, status: 'met' },
      { id: 'm3', label: 'KYC clearance (all investors)', target: 198, achieved: 198, status: 'met' },
    ],
    status: 'pending-approval',
    priority: 'critical',
    submittedBy: 'System Auto-Trigger',
    submittedAt: '2026-08-20 09:00 UTC',
    dueDate: '2026-08-25',
    complianceCleared: true,
    kycCleared: true,
    escrowReleased: false,
    bankDetails: 'Barclays Escrow → ClearPath Renewables Ltd — Sort: 20-18-53 / Acc: 73841920',
    notes: 'All milestones met. Campaign closed at 98% of target. Awaiting Finance Officer approval to execute transfer.',
    auditLog: [
      { ts: '2026-08-20 09:00', actor: 'System', action: 'Settlement batch created on campaign close trigger' },
      { ts: '2026-08-20 09:05', actor: 'Compliance Engine', action: 'Compliance sign-off confirmed — no outstanding flags' },
      { ts: '2026-08-20 09:07', actor: 'KYC Engine', action: 'All 198 investors KYC-cleared' },
    ],
  },
  {
    id: 'sp-002',
    batchRef: 'SP-20260820-006',
    campaign: 'Meridian Logistics Hub — Series B',
    issuer: 'Meridian Infrastructure PLC',
    amount: '$3,200,000',
    amountRaw: 3200000,
    investors: 42,
    milestones: [
      { id: 'm1', label: 'Milestone 1 — Site acquisition (25%)', target: 3125000, achieved: 3200000, status: 'met' },
      { id: 'm2', label: 'Compliance sign-off', target: 1, achieved: 1, status: 'met' },
      { id: 'm3', label: 'KYC clearance', target: 42, achieved: 40, status: 'partial' },
    ],
    status: 'on-hold',
    priority: 'high',
    submittedBy: 'J. Harrington',
    submittedAt: '2026-08-19 14:30 UTC',
    dueDate: '2026-08-28',
    complianceCleared: true,
    kycCleared: false,
    escrowReleased: false,
    bankDetails: 'HSBC Trustee → Meridian Infrastructure PLC — Sort: 40-02-50 / Acc: 12847391',
    notes: '2 investors pending KYC re-verification. Settlement on hold until KYC clearance achieved for all 42 investors.',
    auditLog: [
      { ts: '2026-08-19 14:30', actor: 'J. Harrington', action: 'Settlement batch submitted for Milestone 1 tranche' },
      { ts: '2026-08-19 15:00', actor: 'KYC Engine', action: 'KYC check: 40/42 cleared — 2 investors flagged for re-verification' },
      { ts: '2026-08-19 15:05', actor: 'System', action: 'Settlement placed on hold — KYC incomplete' },
    ],
  },
  {
    id: 'sp-003',
    batchRef: 'SP-20260818-005',
    campaign: 'Thornfield RE Fund II — Q2 Distribution',
    issuer: 'Thornfield Capital Management',
    amount: '$287,440',
    amountRaw: 287440,
    investors: 142,
    milestones: [
      { id: 'm1', label: 'Q2 income distribution trigger', target: 1, achieved: 1, status: 'met' },
      { id: 'm2', label: 'Distribution calculation verified', target: 1, achieved: 1, status: 'met' },
      { id: 'm3', label: 'Investor bank details confirmed', target: 142, achieved: 142, status: 'met' },
    ],
    status: 'pending-approval',
    priority: 'normal',
    submittedBy: 'J. Harrington',
    submittedAt: '2026-08-18 11:00 UTC',
    dueDate: '2026-08-25',
    complianceCleared: true,
    kycCleared: true,
    escrowReleased: true,
    bankDetails: 'Deutsche Bank Trust → 142 investor accounts (batch BACS)',
    notes: 'Quarterly income distribution. All conditions met. Batch BACS file prepared and ready for execution.',
    auditLog: [
      { ts: '2026-08-18 11:00', actor: 'J. Harrington', action: 'Distribution batch submitted — Q2 income trigger' },
      { ts: '2026-08-18 11:30', actor: 'Finance System', action: 'Distribution amounts calculated and verified' },
      { ts: '2026-08-18 12:00', actor: 'Compliance Engine', action: 'Distribution cleared — no compliance flags' },
    ],
  },
  {
    id: 'sp-004',
    batchRef: 'SP-20260815-003',
    campaign: 'Ashford Commercial REIT I — Q2 Distribution',
    issuer: 'Ashford Capital Partners',
    amount: '$412,000',
    amountRaw: 412000,
    investors: 87,
    milestones: [
      { id: 'm1', label: 'Q2 income distribution trigger', target: 1, achieved: 1, status: 'met' },
      { id: 'm2', label: 'Compliance sign-off', target: 1, achieved: 1, status: 'met' },
      { id: 'm3', label: 'Investor bank details confirmed', target: 87, achieved: 87, status: 'met' },
    ],
    status: 'completed',
    priority: 'normal',
    submittedBy: 'J. Harrington',
    submittedAt: '2026-08-15 10:00 UTC',
    dueDate: '2026-08-16',
    complianceCleared: true,
    kycCleared: true,
    escrowReleased: true,
    bankDetails: 'Barclays Escrow → 87 investor accounts (batch BACS)',
    notes: 'Completed successfully. All 87 investors received Q2 distribution on 2026-08-16.',
    auditLog: [
      { ts: '2026-08-15 10:00', actor: 'J. Harrington', action: 'Distribution batch submitted' },
      { ts: '2026-08-15 14:22', actor: 'M. Okafor (Finance Officer)', action: 'Approved — all conditions verified' },
      { ts: '2026-08-16 09:00', actor: 'System', action: 'BACS batch executed — 87 payments dispatched' },
      { ts: '2026-08-16 17:00', actor: 'System', action: 'All 87 payments confirmed received — settlement complete' },
    ],
  },
  {
    id: 'sp-005',
    batchRef: 'SP-20260812-001',
    campaign: 'Vantage Credit Opp. Fund — Investor Refund',
    issuer: 'Vantage Asset Management',
    amount: '$45,000',
    amountRaw: 45000,
    investors: 3,
    milestones: [
      { id: 'm1', label: 'Refund authorisation', target: 1, achieved: 1, status: 'met' },
      { id: 'm2', label: 'Bank details verified', target: 3, achieved: 2, status: 'partial' },
    ],
    status: 'rejected',
    priority: 'high',
    submittedBy: 'System',
    submittedAt: '2026-08-12 16:22 UTC',
    dueDate: '2026-08-14',
    complianceCleared: false,
    kycCleared: true,
    escrowReleased: false,
    bankDetails: 'Suspense Account — bank reject on 1 of 3 accounts',
    notes: 'Rejected: 1 investor bank account returned BACS reject code ARUDD. Requires manual bank detail re-verification before re-submission.',
    auditLog: [
      { ts: '2026-08-12 16:22', actor: 'System', action: 'Refund batch auto-created on subscription cancellation' },
      { ts: '2026-08-13 09:00', actor: 'M. Okafor (Finance Officer)', action: 'Approved for execution' },
      { ts: '2026-08-13 09:30', actor: 'System', action: 'BACS batch submitted — 3 payments' },
      { ts: '2026-08-13 17:00', actor: 'System', action: 'BACS reject received — 1 payment failed (ARUDD code)' },
      { ts: '2026-08-14 10:00', actor: 'M. Okafor (Finance Officer)', action: 'Batch rejected — manual re-verification required' },
    ],
  },
];

const statusConfig: Record<string, { label: string; color: string; bg: string; border: string }> = {
  'pending-approval': { label: 'Pending Approval', color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30' },
  'approved': { label: 'Approved', color: 'text-positive', bg: 'bg-positive/10', border: 'border-positive/30' },
  'executing': { label: 'Executing', color: 'text-info', bg: 'bg-info/10', border: 'border-info/30' },
  'completed': { label: 'Completed', color: 'text-positive', bg: 'bg-positive/10', border: 'border-positive/30' },
  'rejected': { label: 'Rejected', color: 'text-negative', bg: 'bg-negative/10', border: 'border-negative/30' },
  'on-hold': { label: 'On Hold', color: 'text-muted-foreground', bg: 'bg-surface-3', border: 'border-border' },
};

const priorityConfig: Record<string, { color: string; dot: string }> = {
  critical: { color: 'text-negative', dot: 'bg-negative' },
  high: { color: 'text-warning', dot: 'bg-warning' },
  normal: { color: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};

// ─── Approval Modal ───────────────────────────────────────────────────────────
interface ApprovalModalProps {
  item: SettlementItem;
  action: 'approve' | 'reject';
  onConfirm: (notes: string) => void;
  onClose: () => void;
}

function ApprovalModal({ item, action, onConfirm, onClose }: ApprovalModalProps) {
  const [notes, setNotes] = useState('');
  const isApprove = action === 'approve';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="glass-card rounded-2xl w-full max-w-lg border border-border shadow-2xl">
        <div className={`px-6 py-4 border-b border-border flex items-center gap-3 ${isApprove ? 'bg-positive/5' : 'bg-negative/5'}`}>
          {isApprove
            ? <CheckCircle size={20} className="text-positive" />
            : <XCircle size={20} className="text-negative" />}
          <div>
            <p className="font-bold text-foreground">{isApprove ? 'Approve & Execute Transfer' : 'Reject Settlement Batch'}</p>
            <p className="text-xs text-muted-foreground">{item.batchRef} · {item.campaign}</p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="glass-card rounded-xl p-4 bg-surface-3/50 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Transfer Amount</span>
              <span className="font-bold font-mono-data text-foreground">{item.amount}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Beneficiary</span>
              <span className="font-medium text-foreground">{item.issuer}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Investors Affected</span>
              <span className="font-mono-data text-foreground">{item.investors}</span>
            </div>
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">Bank Details</p>
              <p className="text-xs text-foreground mt-0.5 font-mono-data">{item.bankDetails}</p>
            </div>
          </div>

          {isApprove && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <AlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
              <p className="text-xs text-warning">
                This action will initiate an irreversible fund transfer. Verify all details before confirming.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              {isApprove ? 'Approval Notes (optional)' : 'Rejection Reason (required)'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={isApprove ? 'Add any approval notes...' : 'State reason for rejection...'}
              rows={3}
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-accent/50"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button
            onClick={() => onConfirm(notes)}
            disabled={!isApprove && !notes.trim()}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isApprove
                ? 'bg-positive/15 text-positive hover:bg-positive/25 border border-positive/30' :'bg-negative/15 text-negative hover:bg-negative/25 border border-negative/30'
            }`}
          >
            {isApprove ? <><CheckCircle size={14} /> Confirm Approval</> : <><XCircle size={14} /> Confirm Rejection</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Settlement Row ───────────────────────────────────────────────────────────
interface SettlementRowProps {
  item: SettlementItem;
  onApprove: (item: SettlementItem) => void;
  onReject: (item: SettlementItem) => void;
}

function SettlementRow({ item, onApprove, onReject }: SettlementRowProps) {
  const [expanded, setExpanded] = useState(false);
  const sc = statusConfig[item.status];
  const pc = priorityConfig[item.priority];
  const allMilestonesMet = item.milestones.every((m) => m.status === 'met');
  const conditionsMet = item.complianceCleared && item.kycCleared;

  return (
    <div className={`border border-border rounded-xl overflow-hidden transition-all ${
      item.status === 'pending-approval' ? 'border-l-4 border-l-warning' :
      item.status === 'on-hold' ? 'border-l-4 border-l-muted-foreground' :
      item.status === 'rejected' ? 'border-l-4 border-l-negative' :
      item.status === 'completed' ? 'border-l-4 border-l-positive' : ''
    }`}>
      {/* Row Header */}
      <div className="glass-card px-4 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="font-mono-data text-xs text-accent">{item.batchRef}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${sc.bg} ${sc.color} ${sc.border}`}>
                {item.status === 'completed' && <CheckCircle size={10} />}
                {item.status === 'rejected' && <XCircle size={10} />}
                {item.status === 'executing' && <Activity size={10} />}
                {item.status === 'pending-approval' && <Clock size={10} />}
                {item.status === 'on-hold' && <Lock size={10} />}
                {sc.label}
              </span>
              <span className={`inline-flex items-center gap-1 text-xs font-medium ${pc.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
                {item.priority.toUpperCase()}
              </span>
            </div>
            <p className="font-semibold text-foreground truncate">{item.campaign}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{item.issuer}</p>
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mt-2">
              <span>Investors: <span className="font-mono-data text-foreground">{item.investors}</span></span>
              <span>Submitted: <span className="font-mono-data">{item.submittedAt}</span></span>
              <span>Due: <span className="font-mono-data text-foreground">{item.dueDate}</span></span>
              <span>By: {item.submittedBy}</span>
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="text-2xl font-bold font-mono-data text-foreground">{item.amount}</p>
            <div className="flex items-center gap-1.5 mt-2 justify-end flex-wrap">
              {/* Condition indicators */}
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${item.complianceCleared ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'}`}>
                <Shield size={10} /> Compliance
              </span>
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${item.kycCleared ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'}`}>
                <CheckCircle size={10} /> KYC
              </span>
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${allMilestonesMet ? 'bg-positive/10 text-positive' : 'bg-warning/10 text-warning'}`}>
                <Flag size={10} /> Milestones
              </span>
            </div>
            <div className="flex items-center gap-2 mt-3 justify-end">
              {item.status === 'pending-approval' && conditionsMet && (
                <>
                  <button
                    onClick={() => onApprove(item)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-positive/15 text-positive hover:bg-positive/25 border border-positive/20 transition-colors"
                  >
                    <CheckCircle size={12} /> Approve
                  </button>
                  <button
                    onClick={() => onReject(item)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-negative/15 text-negative hover:bg-negative/25 border border-negative/20 transition-colors"
                  >
                    <XCircle size={12} /> Reject
                  </button>
                </>
              )}
              {item.status === 'pending-approval' && !conditionsMet && (
                <span className="text-xs text-warning flex items-center gap-1">
                  <AlertCircle size={12} /> Conditions not met
                </span>
              )}
              <button
                onClick={() => setExpanded(!expanded)}
                className="btn-ghost p-1.5 text-muted-foreground"
                title="Toggle details"
              >
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Detail */}
      {expanded && (
        <div className="border-t border-border bg-surface-3/30 px-4 py-4 space-y-5">
          {/* Milestones */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Fundraising Milestones</p>
            <div className="space-y-2">
              {item.milestones.map((m) => (
                <div key={m.id} className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    m.status === 'met' ? 'bg-positive/20 text-positive' :
                    m.status === 'partial'? 'bg-warning/20 text-warning' : 'bg-negative/20 text-negative'
                  }`}>
                    {m.status === 'met' ? <CheckCircle size={11} /> : m.status === 'partial' ? <AlertTriangle size={11} /> : <XCircle size={11} />}
                  </div>
                  <p className="text-sm text-foreground flex-1">{m.label}</p>
                  <span className={`text-xs font-medium ${
                    m.status === 'met' ? 'text-positive' : m.status === 'partial' ? 'text-warning' : 'text-negative'
                  }`}>
                    {m.status === 'met' ? 'Met' : m.status === 'partial' ? 'Partial' : 'Unmet'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Bank Details & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Transfer Details</p>
              <div className="glass-card rounded-lg p-3 bg-surface-3/50">
                <p className="text-xs font-mono-data text-foreground">{item.bankDetails}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Notes</p>
              <div className="glass-card rounded-lg p-3 bg-surface-3/50">
                <p className="text-xs text-muted-foreground">{item.notes}</p>
              </div>
            </div>
          </div>

          {/* Audit Log */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Audit Trail</p>
            <div className="space-y-2">
              {item.auditLog.map((entry, i) => (
                <div key={i} className="flex items-start gap-3 text-xs">
                  <span className="font-mono-data text-muted-foreground flex-shrink-0 w-36">{entry.ts}</span>
                  <span className="text-accent font-medium flex-shrink-0">{entry.actor}</span>
                  <span className="text-foreground">{entry.action}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SettlementProcessor() {
  const [settlements, setSettlements] = useState<SettlementItem[]>(mockSettlements);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [approvalModal, setApprovalModal] = useState<{ item: SettlementItem; action: 'approve' | 'reject' } | null>(null);
  const [realtimeAlerts, setRealtimeAlerts] = useState<{ id: string; type: string; message: string; ts: string }[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  const { alerts: liveAlerts, isConnected: liveConnected, unreadCount, markAllRead, markRead, dismissAlert } = useRealtimeAlerts({
    channels: ['settlements', 'transactions', 'realtime_alerts'],
  });

  // ─── Real-time subscription (settlement_batches + milestones for local state) ─
  useEffect(() => {
    let supabase: any = null;
    let channel: any = null;

    try {
      supabase = createClient();
      if (!supabase) return;

      channel = supabase
        .channel('settlement-processor-rt')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'settlement_batches' }, (payload: any) => {
          setRealtimeAlerts((prev) => [
            {
              id: `rt-${Date.now()}`,
              type: 'settlement',
              message: `Settlement batch ${payload.eventType?.toLowerCase()}: ${payload.new?.batch_ref || payload.old?.batch_ref || 'unknown'}`,
              ts: new Date().toLocaleTimeString(),
            },
            ...prev.slice(0, 4),
          ]);
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'campaign_milestones' }, (payload: any) => {
          setRealtimeAlerts((prev) => [
            {
              id: `rt-${Date.now()}`,
              type: 'milestone',
              message: `Milestone update: ${payload.new?.label || 'Campaign milestone changed'}`,
              ts: new Date().toLocaleTimeString(),
            },
            ...prev.slice(0, 4),
          ]);
        })
        .subscribe((status: string) => {
          setIsConnected(status === 'SUBSCRIBED');
        });
    } catch {
      // Supabase not configured — run in demo mode
    }

    return () => {
      if (supabase && channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleApprove = useCallback((item: SettlementItem) => {
    setApprovalModal({ item, action: 'approve' });
  }, []);

  const handleReject = useCallback((item: SettlementItem) => {
    setApprovalModal({ item, action: 'reject' });
  }, []);

  const handleConfirm = useCallback((notes: string) => {
    if (!approvalModal) return;
    const { item, action } = approvalModal;
    const newStatus = action === 'approve' ? 'executing' : 'rejected';
    const actor = 'Finance Officer (Current User)';
    const actionText = action === 'approve'
      ? `Approved for execution${notes ? ` — ${notes}` : ''}`
      : `Rejected — ${notes}`;

    setSettlements((prev) =>
      prev.map((s) =>
        s.id === item.id
          ? {
              ...s,
              status: newStatus as any,
              auditLog: [
                ...s.auditLog,
                { ts: new Date().toISOString().slice(0, 16).replace('T', ' '), actor, action: actionText },
              ],
            }
          : s
      )
    );

    setRealtimeAlerts((prev) => [
      {
        id: `local-${Date.now()}`,
        type: action === 'approve' ? 'approval' : 'rejection',
        message: `${item.batchRef} ${action === 'approve' ? 'approved — executing transfer' : 'rejected'}`,
        ts: new Date().toLocaleTimeString(),
      },
      ...prev.slice(0, 4),
    ]);

    setApprovalModal(null);
  }, [approvalModal]);

  // ─── Derived data ──────────────────────────────────────────────────────────
  const filtered = settlements.filter((s) => {
    const matchStatus = filterStatus === 'all' || s.status === filterStatus;
    const matchSearch = !searchQuery || s.campaign.toLowerCase().includes(searchQuery.toLowerCase()) || s.batchRef.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });

  const pendingCount = settlements.filter((s) => s.status === 'pending-approval').length;
  const totalPending = settlements.filter((s) => s.status === 'pending-approval').reduce((acc, s) => acc + s.amountRaw, 0);
  const executingCount = settlements.filter((s) => s.status === 'executing').length;
  const completedMTD = settlements.filter((s) => s.status === 'completed').reduce((acc, s) => acc + s.amountRaw, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Settlement Processor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Milestone-gated fund transfer approvals · Finance Officer workflow · 20 Aug 2026
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs gap-1.5"><RefreshCw size={12} /> Refresh</button>
          <button className="btn-primary text-xs gap-1.5"><Download size={12} /> Export</button>
        </div>
      </div>

      {/* Live Settlement Feed */}
      <RealtimeFeedPanel
        alerts={liveAlerts}
        isConnected={liveConnected}
        unreadCount={unreadCount}
        onMarkAllRead={markAllRead}
        onMarkRead={markRead}
        onDismiss={dismissAlert}
        label="Live Settlement Updates"
        maxVisible={5}
      />

      {/* Local activity feed (approval actions) */}
      {realtimeAlerts.length > 0 && (
        <div className="glass-card rounded-xl p-3 border border-accent/20 bg-accent/5">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={13} className="text-accent" />
            <p className="text-xs font-semibold text-accent uppercase tracking-widest">Approval Activity</p>
          </div>
          <div className="space-y-1">
            {realtimeAlerts.map((alert) => (
              <div key={alert.id} className="flex items-center gap-3 text-xs">
                <span className="font-mono-data text-muted-foreground flex-shrink-0">{alert.ts}</span>
                <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${
                  alert.type === 'approval' ? 'bg-positive/15 text-positive' :
                  alert.type === 'rejection' ? 'bg-negative/15 text-negative' :
                  alert.type === 'milestone'? 'bg-accent/15 text-accent' : 'bg-info/15 text-info'
                }`}>{alert.type}</span>
                <span className="text-foreground">{alert.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Pending Approval"
          value={String(pendingCount)}
          subValue={`$${(totalPending / 1e6).toFixed(2)}M awaiting sign-off`}
          variant="warning"
          icon={Clock}
          iconColor="text-warning"
        />
        <MetricCard
          label="Total Value Pending"
          value={`$${(totalPending / 1e6).toFixed(2)}M`}
          subValue={`${pendingCount} batches`}
          variant="warning"
          icon={DollarSign}
          iconColor="text-warning"
          mono
        />
        <MetricCard
          label="Executing"
          value={String(executingCount)}
          subValue="Transfers in progress"
          variant="info"
          icon={Activity}
          iconColor="text-info"
        />
        <MetricCard
          label="Completed MTD"
          value={`$${(completedMTD / 1e6).toFixed(2)}M`}
          subValue="Month-to-date settled"
          variant="positive"
          icon={CheckCircle}
          iconColor="text-positive"
          mono
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search batch ref or campaign..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-surface-3 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent/50"
          />
        </div>
        <div className="flex items-center gap-1 bg-surface-3 border border-border rounded-lg p-1">
          {(['all', 'pending-approval', 'on-hold', 'executing', 'completed', 'rejected'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                filterStatus === s ? 'bg-accent text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s === 'all' ? 'All' : statusConfig[s]?.label || s}
              {s === 'pending-approval' && pendingCount > 0 && (
                <span className="ml-1 bg-warning/30 text-warning rounded-full px-1 font-mono-data">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Settlement list */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <p className="text-muted-foreground text-sm">No settlements match the current filter.</p>
          </div>
        ) : (
          filtered.map((item) => (
            <SettlementRow
              key={item.id}
              item={item}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))
        )}
      </div>

      {/* Approval modal */}
      {approvalModal && (
        <ApprovalModal
          item={approvalModal.item}
          action={approvalModal.action}
          onConfirm={handleConfirm}
          onClose={() => setApprovalModal(null)}
        />
      )}
    </div>
  );
}
